
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CropCard from '../../components/crops/CropCard';
import ModuleStrip from '../../components/layout/ModuleStrip';
import { useModule } from '../../context/ModuleContext';
import CategoryStrip from '../../components/layout/CategoryStrip';
import BuyerDashboardPage from './BuyerDashboardPage';

const G     = '#1A6B3C';
const LIGHT = '#EAF3DE';

const SECTION_LABELS = {
  mine:      { icon:'', label:'My Crops' },
  following: { icon:'👥', label:'Following' },
  referral:  { icon:'🎁', label:'From Referrals' },
  nearby:    { icon:'', label:'Nearby Farmers' },
};

const STATUS_PILL = {
  growing:       { bg:'#DCFCE7', color:'#166534', icon:'', label:'Growing' },
  ready_to_sell: { bg:'#D1FAE5', color:'#065F46', icon:'🟢', label:'Ready' },
  sold:          { bg:'#DBEAFE', color:'#1E40AF', icon:'✅', label:'Sold' },
  closed:        { bg:'#F3F4F6', color:'#6B7280', icon:'🔒', label:'Closed' },
};

const BANNERS = [
  { img:'/images/crop-banner.png',  to:'/add-crop' },
  { img:'/images/crop-banner1.png',  to:'/add-crop' },
  
];

export default function DashboardPage() {
  const { user }         = useAuth();
  const { activeModule } = useModule();
  const navigate         = useNavigate();

  // ── Route buyers to dedicated dashboard ──────────────────
  if (user?.role === 'buyer') return <BuyerDashboardPage />;

  const [feed, setFeed]               = useState([]);
  const [sections, setSections]       = useState([]);
  const [myCrops, setMyCrops]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [activeSub, setActiveSub]           = useState('');
  const [banner, setBanner]                 = useState(0);

  const fetchingRef    = useRef(false);
  const observerRef    = useRef(null); // ← IntersectionObserver
  const loaderRef      = useRef(null); // ← sentinel div at bottom
  const pageRef        = useRef(1);
  const hasMoreRef     = useRef(true);
  const categoryRef    = useRef('');
  const subRef         = useRef('');
  const bannerTimer    = useRef();

  useEffect(() => {
    loadFeed(1);
    if (user?.role === 'seller') loadMyCrops();
    bannerTimer.current = setInterval(() => setBanner(b => (b + 1) % BANNERS.length), 4000);
    return () => clearInterval(bannerTimer.current);
  }, []);

  // ── Set up IntersectionObserver ───────────────────────────
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !fetchingRef.current) {
          loadFeed(pageRef.current + 1, categoryRef.current, subRef.current);
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observerRef.current.observe(loaderRef.current);
    return () => observerRef.current?.disconnect();
  }, []);

  const loadFeed = async (p, cat, sub) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (p > 1) setLoadingMore(true);

    const useCat = cat !== undefined ? cat : categoryRef.current;
    const useSub = sub !== undefined ? sub : subRef.current;

    try {
      let url = `/crops/feed?page=${p}&limit=10`;
      if (useCat) url += `&category=${encodeURIComponent(useCat)}`;
      if (useSub) url += `&subCategory=${encodeURIComponent(useSub)}`;
      const { data } = await api.get(url);
      let newCrops = data.crops || [];
      const newSecs  = data.sections || [];

      // ── Client-side filter (safety net in case backend sends wrong data) ──
      if (useCat) {
        newCrops = newCrops.filter(c => {
          const stored = c.category?.toLowerCase().trim() || '';
          const filter = useCat.toLowerCase().trim();
          return stored === filter || stored.includes(filter) || filter.includes(stored);
        });
      }
      if (useSub) {
        newCrops = newCrops.filter(c =>
          c.subCategory?.toLowerCase().includes(useSub.toLowerCase())
        );
      }

      if (p === 1) {
        setFeed(newCrops);
        setSections(newSecs);
      } else {
        setFeed(prev => [...prev, ...newCrops]);
        setSections(prev => [...prev, ...newSecs]);
      }

      const more = newCrops.length === 10;
      setHasMore(more);
      hasMoreRef.current = more;
      setPage(p);
      pageRef.current = p;
    } catch {}
    finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  };

  const loadMyCrops = async () => {
    try {
      const { data } = await api.get('/crops/my?limit=6');
      setMyCrops(data.crops);
    } catch {}
  };

  const handleCategorySelect = (cat) => {
    const c = cat.toLowerCase();
    setActiveCategory(c); setActiveSub('');
    categoryRef.current = c; subRef.current = '';
    hasMoreRef.current = true; pageRef.current = 1;
    loadFeed(1, c, '');
  };

  const handleSubSelect = (cat, sub) => {
    const c = cat.toLowerCase();
    setActiveCategory(c); setActiveSub(sub);
    categoryRef.current = c; subRef.current = sub;
    hasMoreRef.current = true; pageRef.current = 1;
    loadFeed(1, c, sub);
  };

  const clearFilters = () => {
    setActiveCategory(''); setActiveSub('');
    categoryRef.current = ''; subRef.current = '';
    hasMoreRef.current = true; pageRef.current = 1;
    loadFeed(1, '', '');
  };

  const groupedFeed = () => {
    if (!sections.length || user?.role !== 'seller') return [{ section:null, crops:feed }];
    const groups = [];
    let current = null;
    feed.forEach((crop, i) => {
      const sec = sections[i] || 'nearby';
      if (sec !== current) { groups.push({ section:sec, crops:[crop] }); current = sec; }
      else groups[groups.length-1].crops.push(crop);
    });
    return groups;
  };

  return (
    <div style={{ background:'#F5F7FA', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif' }}>
      <style>{`
        @keyframes dashSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ background: activeModule?.color || G, paddingTop:'env(safe-area-inset-top, 0px)',paddingBottom:1 }}>
        <ModuleStrip />
        <div style={{ margin:'3px 18px 2px 12px', display:'flex', alignItems:'center', gap:6 }}>
          <div onClick={() => navigate('/search')}
            style={{ flex:1, background:'rgba(255,255,255,.18)', borderRadius:20, padding:'6px 15px', display:'flex', alignItems:'center', gap:8, cursor:'pointer', border:'1px solid rgba(255,255,255,.25)' }}>
            <span style={{ fontSize:12,color:'rgba(255,255,255,.85)' }}>🔍</span>
            <span style={{ fontSize:13, color:'rgba(255,255,255,.8)' }}>Search crops or farmers...</span>
          </div>
        </div>
      </div>

      {/* Hero banner carousel */}
      <div style={{ padding:'5px 10px 0' }}>
        <div style={{ borderRadius:18, overflow:'hidden', position:'relative', height:170, cursor:'pointer' }}

          // onClick={() => navigate(BANNERS[banner].to || '/add-crop')}
          >
          <img
            src={BANNERS[banner].img}
            alt="banner"
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
            onError={e => {
              e.target.style.display='none';
              e.target.parentNode.style.background=`linear-gradient(135deg,${G} 0%,#2d9e5f 60%,#56c47e 100%)`;
            }}
          />
          {/* Dark overlay for text readability */}
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg,rgba(0,0,0,.45) 0%,rgba(0,0,0,.1) 70%,rgba(0,0,0,0) 100%)' }} />
          {/* Shop button */}
          {/* <button
            onClick={e => { e.stopPropagation(); navigate(BANNERS[banner].to || '/add-crop'); }}
            style={{ position:'absolute', bottom:16, left:16, background:'white', color:G, border:'none', borderRadius:25, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            {BANNERS[banner].btn}
          </button> */}
          {/* Dots */}
          <div style={{ position:'absolute', bottom:20, right:14, display:'flex', gap:5 }}>
            {BANNERS.map((_, i) => (
              <div key={i} onClick={e => { e.stopPropagation(); setBanner(i); }}
                style={{ width:i===banner?16:6, height:6, borderRadius:3, background:i===banner?'white':'rgba(255,255,255,.5)', cursor:'pointer', transition:'all .3s' }} />
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop:6 }}>
        <CategoryStrip
          onCategorySelect={handleCategorySelect}
          onSubCategorySelect={handleSubSelect}
          activeCategory={activeCategory}
        />
      </div>

      {(activeCategory || activeSub) && (
        <div style={{ display:'flex', gap:6, padding:'10px 14px', background:'white', borderBottom:'0.5px solid #E5E7EB', alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:12, color:'#9CA3AF' }}>Showing:</span>
          {activeCategory && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 11px', borderRadius:20, background:LIGHT, color:G, fontSize:12, fontWeight:700 }}>
              {activeCategory.charAt(0).toUpperCase()+activeCategory.slice(1)}
              <button onClick={() => { setActiveCategory(''); categoryRef.current=''; if(!activeSub) clearFilters(); else loadFeed(1,'',activeSub); }}
                style={{ background:'none', border:'none', cursor:'pointer', color:G, fontSize:14, lineHeight:1, padding:0 }}>×</button>
            </span>
          )}
          {activeSub && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 11px', borderRadius:20, background:'#DCFCE7', color:'#166534', fontSize:12, fontWeight:700 }}>
              {activeSub}
              <button onClick={() => { setActiveSub(''); subRef.current=''; loadFeed(1, activeCategory, ''); }}
                style={{ background:'none', border:'none', cursor:'pointer', color:'#166534', fontSize:14, lineHeight:1, padding:0 }}>×</button>
            </span>
          )}
          <button onClick={clearFilters}
            style={{ marginLeft:'auto', fontSize:11, color:'#EF4444', fontWeight:600, background:'none', border:'none', cursor:'pointer' }}>
            Show all ×
          </button>
        </div>
      )}

      <div style={{ padding:'8px 14px 0' }}>
        {user?.role === 'seller' && (
          <div style={{ marginBottom:22 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <span style={{ fontSize:16, fontWeight:800, color:'#1a1a1a', display:'flex', alignItems:'center', gap:6 }}> My Crops</span>
              <button onClick={() => navigate('/my-crops')}
                style={{ background:'none', border:'none', color:G, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                View all →
              </button>
            </div>

            <div style={{ display:'flex', gap:10, overflowX:'auto', scrollbarWidth:'none', paddingBottom:4 }}>
              <div onClick={() => navigate('/add-crop')}
                style={{ flexShrink:0, width:104, height:150, border:'1.5px dashed #BBF7D0', borderRadius:16, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, padding:10, textAlign:'center', cursor:'pointer', background:'white' }}>
                <div style={{ width:34, height:34, borderRadius:'50%', background:G, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:17, fontWeight:700 }}>+</div>
                <span style={{ fontSize:11.5, fontWeight:700, color:G }}>Add Crop</span>
              </div>

              {myCrops.map(crop => {
                const st = STATUS_PILL[crop.status] || STATUS_PILL.growing;
                const thumb = crop.latestStage?.mediaUrls?.[0];
                return (
                  <div key={crop._id} onClick={() => navigate(`/crop/${crop._id}`)}
                    style={{ flexShrink:0, width:104, background:'white', borderRadius:16, border:'0.5px solid #E5E7EB', overflow:'hidden', cursor:'pointer' }}>
                    <div style={{ height:78, background:LIGHT, position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {thumb
                        ? <img src={thumb} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none'; }} />
                        : <span style={{ fontSize:30 }}></span>}
                      <div style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,.6)', color:'white', fontSize:9.5, fontWeight:700, padding:'2px 7px', borderRadius:10 }}>
                        D{crop.currentDayCount}
                      </div>
                    </div>
                    <div style={{ padding:'8px 9px 10px' }}>
                      <div style={{ fontSize:12.5, fontWeight:700, color:'#1a1a1a', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{crop.cropName}</div>
                      <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:4 }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background:st.color, display:'inline-block' }} />
                        <span style={{ fontSize:10, fontWeight:600, color:st.color }}>{st.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
            <div style={{ width:28, height:28, border:'3px solid #E5E7EB', borderTopColor:G, borderRadius:'50%', animation:'dashSpin 0.8s linear infinite' }} />
          </div>
        ) : feed.length === 0 ? (
          <div style={{ textAlign:'center', padding:'50px 20px', color:'#9CA3AF' }}>
            <div style={{ fontSize:48, marginBottom:12 }}></div>
            <div style={{ fontSize:15, fontWeight:700, color:'#4B5D4F' }}>Your feed is empty</div>
            <div style={{ fontSize:12.5, marginTop:4, marginBottom:18 }}>Add your first crop or follow farmers to see their updates</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              <button onClick={() => navigate('/add-crop')}
                style={{ background:G, color:'white', border:'none', borderRadius:20, padding:'10px 18px', fontSize:12.5, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 12px rgba(26,107,60,.28)' }}>
                 Add Crop
              </button>
              <button onClick={() => navigate('/farmers')}
                style={{ background:'white', color:G, border:`1.5px solid ${G}`, borderRadius:20, padding:'10px 18px', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                👨‍🌾 Find Farmers
              </button>
            </div>
          </div>
        ) : (
          <>
            {groupedFeed().map((group, gi) => (
              <div key={gi}>
                {group.section && SECTION_LABELS[group.section] && (
                  <div style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 2px', fontSize:12.5, fontWeight:700, color:'#4B5D4F' }}>
                    <span>{SECTION_LABELS[group.section].icon}</span>
                    <span>{SECTION_LABELS[group.section].label}</span>
                    <span style={{ fontSize:11, color:'#9CA3AF', fontWeight:500 }}>{group.crops.length} crop{group.crops.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {group.crops.map(crop => <CropCard key={crop._id} crop={crop} />)}
              </div>
            ))}

            {/* ── Sentinel div — triggers infinite scroll ── */}
            <div ref={loaderRef} style={{ height:60, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {loadingMore && (
                <div style={{ width:22, height:22, border:'3px solid #E5E7EB', borderTopColor:G, borderRadius:'50%', animation:'dashSpin 0.8s linear infinite' }} />
              )}
              {!loadingMore && !hasMore && feed.length > 0 && (
                <div style={{ fontSize:12, color:'#9CA3AF', textAlign:'center' }}>
                  ✅ You've seen all crops
                </div>
              )}
            </div>
          </>
        )}
        <div style={{ height:20 }} />
      </div>
    </div>
  );
}